import {PageStandard} from "../utils/PageStandard";
import {Fragment, useContext, useEffect, useMemo, useState} from "react";
import {FeedbackContext} from "../utils/FeedbackContext";
import {Traduzione, TraduzioniModel} from "../utils/Model";
import {
    Accordion,
    AccordionDetails,
    AccordionGroup,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Input,
    Stack,
    Typography,
} from "@mui/joy";
import {getTraduzioni, uploadTraduzioni} from "../utils/ViewModel";
import {FileDownload, Search, UnfoldLess, UnfoldMore} from "@mui/icons-material";
import {downloadJSON} from "../utils/Utils";
import moment from "moment";

const LANGUAGES: { key: keyof Traduzione, label: string, flag: string }[] = [
    {key: "en", label: "Inglese", flag: "🇬🇧"},
    {key: "fr", label: "Francese", flag: "🇫🇷"},
    {key: "ar", label: "Arabo", flag: "🇸🇦"},
    {key: "es", label: "Spagnolo", flag: "🇪🇸"},
]

const CATEGORY_LABELS: Record<keyof TraduzioniModel, string> = {
    categorieTitles: "Titoli categorie",
    serviziTitles: "Titoli servizi",
    booking: "Prenotazioni",
    notes: "Note",
    openingHours: "Orari di apertura",
}

const CATEGORY_ORDER: (keyof TraduzioniModel)[] = ["categorieTitles", "serviziTitles", "booking", "notes", "openingHours"]

export function Traduzioni() {

    const {setMessage} = useContext(FeedbackContext)
    const [traduzioni, setTraduzioni] = useState<TraduzioniModel | null>(null)
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState("")
    const [expanded, setExpanded] = useState<Set<string>>(new Set())

    useEffect(() => {
        getTraduzioni().then(data => {
            setTraduzioni({
                categorieTitles: data?.categorieTitles ?? {},
                serviziTitles: {},
                booking: data?.booking ?? {},
                notes: data?.notes ?? {},
                openingHours: data?.openingHours ?? {},
            })
        })
    }, []);

    // Espande di default tutte le categorie che contengono dati, al primo caricamento
    useEffect(() => {
        if (!traduzioni) return
        setExpanded(prev => {
            if (prev.size > 0) return prev
            const withData = CATEGORY_ORDER.filter(c => Object.keys(traduzioni[c]).length > 0)
            return new Set(withData)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [traduzioni !== null]);

    const searchLower = search.trim().toLowerCase()

    const categorie = useMemo(() => {
        if (!traduzioni) return []
        return CATEGORY_ORDER
            .map(categoria => {
                const entries = Object.entries(traduzioni[categoria])
                    .filter(([key]) => searchLower === "" || key.toLowerCase().includes(searchLower))
                return {categoria, entries}
            })
            .filter(c => c.entries.length > 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [traduzioni, searchLower])

    const globalStats = useMemo(() => getStats(traduzioni ? CATEGORY_ORDER.flatMap(c => Object.values(traduzioni[c])) : []), [traduzioni])

    if (traduzioni === null) {
        return (
            <PageStandard title="Traduzioni" sidebarVisible={true}>
                <Stack alignItems="center" justifyContent="center" sx={{flex: 1}}>
                    <CircularProgress/>
                </Stack>
            </PageStandard>
        )
    }

    return (
        <PageStandard title="Traduzioni" sidebarVisible={true} headerEnd={
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <CircularProgress determinate value={globalStats.percentage}
                                       color={colorForPercentage(globalStats.percentage)}/>
                    <Typography><b>{globalStats.percentage} %</b> completato</Typography>
                </Stack>
                <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<FileDownload/>}
                    onClick={() => downloadJSON(traduzioni, `oasi-traduzioni-backup-${moment().format("YYYY-MM-DD")}.json`)}
                >
                    Backup traduzioni
                </Button>
                <Button onClick={async () => {
                    if (window.confirm("Sicuro?")) {
                        setLoading(true)
                        const res = await uploadTraduzioni(traduzioni)
                        if (res) setMessage(res.message)
                        setLoading(false)
                    }
                }}
                        disabled={loading}
                >
                    {loading ? <CircularProgress color="neutral"/> : "Salva traduzioni"}
                </Button>
            </Stack>
        }>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}} flexWrap="wrap">
                <Input
                    placeholder="Cerca una stringa..."
                    startDecorator={<Search/>}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{minWidth: 280, flexGrow: 1, maxWidth: 420}}
                />
                <Button size="sm" variant="plain" color="neutral" startDecorator={<UnfoldMore/>}
                        onClick={() => setExpanded(new Set(categorie.map(c => c.categoria)))}
                >
                    Espandi tutto
                </Button>
                <Button size="sm" variant="plain" color="neutral" startDecorator={<UnfoldLess/>}
                        onClick={() => setExpanded(new Set())}
                >
                    Comprimi tutto
                </Button>
            </Stack>

            {
                categorie.length === 0 &&
                <Typography level="body-md" sx={{textAlign: "center", py: 4}}>
                    Nessuna stringa trovata{searchLower !== "" ? ` per "${search}"` : ""}.
                </Typography>
            }

            <AccordionGroup sx={{maxWidth: "100%"}} transition="0.2s">
                {categorie.map(({categoria, entries}) => {
                    const stats = getStats(entries.map(([, trad]) => trad))
                    const isOpen = expanded.has(categoria)
                    return (
                        <Accordion
                            key={categoria}
                            expanded={isOpen}
                            onChange={(_, exp) => {
                                setExpanded(prev => {
                                    const next = new Set(prev)
                                    if (exp) next.add(categoria); else next.delete(categoria)
                                    return next
                                })
                            }}
                        >
                            <AccordionSummary>
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{flex: 1, pr: 1}}>
                                    <Typography level="title-md" fontWeight="bold">
                                        {CATEGORY_LABELS[categoria]}
                                    </Typography>
                                    <Chip size="sm" variant="soft" color="neutral">
                                        {entries.length} {entries.length === 1 ? "stringa" : "stringhe"}
                                    </Chip>
                                    <Box sx={{flex: 1}}/>
                                    <Chip size="sm" variant="solid" color={colorForPercentage(stats.percentage)}>
                                        {stats.percentage}%
                                    </Chip>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{overflowX: "auto", pt: 0.5, pb: 1}}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "minmax(220px, 1.3fr) repeat(4, minmax(170px, 1fr))",
                                            minWidth: 900,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: "md",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {/* Intestazione colonne: sempre visibile, non serve scroll per capire quale lingua è quale */}
                                        <Box sx={HEADER_CELL_SX}>
                                            🇮🇹 Stringa da tradurre
                                        </Box>
                                        {LANGUAGES.map(l => (
                                            <Box key={l.key} sx={HEADER_CELL_SX}>
                                                {l.flag} {l.label}
                                            </Box>
                                        ))}

                                        {entries.map(([key, trad], idx) => {
                                            const rowStats = getStats([trad])
                                            const accentColor = colorForPercentage(rowStats.percentage)
                                            const zebraSx = idx % 2 === 1 ? {bgcolor: "background.level1"} : undefined
                                            return (
                                                <Fragment key={key}>
                                                    <Box sx={{...CELL_SX, ...zebraSx, gap: 1}}>
                                                        <Box sx={{
                                                            width: 8,
                                                            height: 8,
                                                            flexShrink: 0,
                                                            borderRadius: "50%",
                                                            bgcolor: `${accentColor}.400`,
                                                        }}/>
                                                        <Typography level="body-sm">{key}</Typography>
                                                    </Box>
                                                    {LANGUAGES.map(({key: lang}) => (
                                                        <Box key={lang} sx={{...CELL_SX, ...zebraSx}}>
                                                            <Input
                                                                type="text"
                                                                size="sm"
                                                                value={trad[lang] ?? ""}
                                                                onChange={e => handleChange(categoria, key, lang, e.target.value)}
                                                                error={!trad[lang]}
                                                                disabled={loading}
                                                                slotProps={lang === "ar" ? {input: {dir: "rtl"}} : undefined}
                                                                sx={{width: "100%", ...(lang === "ar" ? {textAlign: "right"} : {})}}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Fragment>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </AccordionGroup>
        </PageStandard>
    )

    function handleChange(
        categoria: keyof TraduzioniModel,
        key: string,
        lang: keyof Traduzione,
        value: string
    ) {
        setTraduzioni(prev => {
            if (!prev) return prev;

            // Clona profondamente solo la parte da modificare per mantenere immutabilità
            const newTraduzioni = {...prev};
            const newCategoria = {...newTraduzioni[categoria]};
            const newTrad = {...newCategoria[key]};

            newTrad[lang] = value.trim() === "" ? null : value; // se vuoto metto null

            newCategoria[key] = newTrad;
            newTraduzioni[categoria] = newCategoria;

            return newTraduzioni;
        });
    }
}

const HEADER_CELL_SX = {
    px: 1.5,
    py: 1,
    fontWeight: "lg",
    fontSize: "sm",
    bgcolor: "background.level2",
    borderBottom: "1px solid",
    borderColor: "divider",
} as const

const CELL_SX = {
    px: 1.5,
    py: 0.75,
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid",
    borderColor: "divider",
} as const

function getStats(entries: Traduzione[]): { total: number, translated: number, percentage: number } {
    const total = entries.length
    const translated = entries.filter(trad => trad.en || trad.fr || trad.ar || trad.es).length
    const percentage = total === 0 ? 0 : Math.round((translated / total) * 100)
    return {total, translated, percentage}
}

function colorForPercentage(percentage: number): "success" | "warning" | "danger" | "neutral" {
    if (percentage >= 100) return "success"
    if (percentage >= 50) return "warning"
    if (percentage > 0) return "danger"
    return "neutral"
}
